import {
  BenefitCoverage,
  buildCoverageActionRecord,
  findLatestCoverageRequest,
  isCoverageRequest,
  resolveBenefitCoverage,
} from '@bariendo/lib/benefit-events';
import { CoverageAction, PriorAuthRequirement, Track } from '@bariendo/lib/constants';
import { EMPLOYER_EXTENSION } from '@bariendo/lib/extensions';
import { createReference, getExtension, getReferenceString, MedplumClient } from '@medplum/core';
import { Patient, Practitioner } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import type {
  CoverageActivityConfig,
  CoverageActivityValues,
} from '@/components/Timeline/TimelineMessageBox/CoverageActivityBox';
import { EmployerCoverageFields, EmployerPayor } from '@/components/Timeline/TimelineMessageBox/EmployerCoverageFields';
import { InsuranceCoverageFields } from '@/components/Timeline/TimelineMessageBox/InsuranceCoverageFields';
import { updateInsuranceCoverage } from '@/components/Timeline/TimelineItems/PatientAccessCall/insuranceVerification';
import { writePatientEmployer } from '@/components/Timeline/TimelineItems/PatientAccessCall/patientEmployer';
import { switchPatientTrack } from '@/components/Timeline/TimelineItems/PatientAccessCall/trackSwitch';
import { getConfig } from '@/config';
import { readInsuranceSection } from '@/pages/PatientTimelinePage/PatientSummary/Insurance/insuranceDetails';
import type { InsuranceEdit } from '@/pages/PatientTimelinePage/PatientSummary/Insurance/useInsuranceSection';
import { usePatientTrack } from '@/hooks/usePatientTrack';
import { useTimelineContext } from '@/hooks/useTimelineContext';

/** The employer steps, in the spec's order and words (`timeline-employer-card.mdx`). */
export const EMPLOYER_COVERAGE_ACTIONS: { value: CoverageAction; label: string }[] = [
  { value: CoverageAction.REFERRAL_SENT, label: 'Referral sent' },
  { value: CoverageAction.NO_BENEFIT, label: 'No benefit' },
  { value: CoverageAction.BENEFIT_CONFIRMED, label: 'Benefit confirmed' },
  { value: CoverageAction.CASE_FORM_SUBMITTED, label: 'Case form submitted' },
  { value: CoverageAction.APPROVED, label: 'Approved' },
  { value: CoverageAction.APPROVAL_DENIED, label: 'Approval denied' },
];

/** The insurance steps, in the spec's order and words (`timeline-insurance-card.mdx`). */
export const INSURANCE_COVERAGE_ACTIONS: { value: CoverageAction; label: string }[] = [
  { value: CoverageAction.BENEFIT_VERIFIED, label: 'Benefit verified' },
  { value: CoverageAction.NO_BENEFIT, label: 'No benefit' },
  { value: CoverageAction.QUESTIONNAIRE_RECEIVED, label: 'Questionnaire received' },
  { value: CoverageAction.PSYCH_EVALUATION_RECEIVED, label: 'Psych evaluation received' },
  { value: CoverageAction.PRIOR_AUTH_SUBMITTED, label: 'Prior auth submitted' },
  { value: CoverageAction.APPROVED, label: 'Approved' },
  { value: CoverageAction.PRIOR_AUTH_DENIED, label: 'Prior auth denied' },
];

const DOCUMENT_TYPE_BY_ACTION: Partial<Record<string, PriorAuthRequirement>> = {
  [CoverageAction.QUESTIONNAIRE_RECEIVED]: PriorAuthRequirement.QUESTIONNAIRE,
  [CoverageAction.PSYCH_EVALUATION_RECEIVED]: PriorAuthRequirement.PSYCH_EVALUATION,
};

/** The employer tracks the composer serves: an unverified employer, or a known administrator. */
const EMPLOYER_TRACKS: ReadonlySet<Track | undefined> = new Set([Track.EMPLOYER, Track.CARRUM, Track.TRANSCARENT]);

const payorOf = (track: Track | undefined): EmployerPayor | null =>
  track === Track.CARRUM || track === Track.TRANSCARENT ? track : null;

/**
 * The employer track's config (BAR-3076, BAR-3077). On Record: a changed employer is saved,
 * a changed payor switches the track (a new episode whose Coverage names the administrator),
 * then the step is written as the signed-in user, so `meta.author` is the actor. A payor's
 * answer points at the latest request.
 */
export function employerCoverageConfig(input: {
  medplum: MedplumClient;
  patient: Patient;
  profile: Practitioner | undefined;
  track: Track | undefined;
  payor: EmployerPayor | null;
  employer: string;
  providerOrganizationId: string;
  trackFields?: ReactNode;
  onRecorded?: () => void | Promise<void>;
}): CoverageActivityConfig {
  const { medplum, patient, profile, track, payor, employer, providerOrganizationId, trackFields, onRecorded } = input;
  return {
    actions: EMPLOYER_COVERAGE_ACTIONS,
    trackFields,
    ready: payor !== null,
    onRecord: async ({ action, date, note, documents }) => {
      if (!payor) throw new Error('Select a payor first');
      const patientId = patient.id as string;
      if (employer.trim()) await writePatientEmployer(medplum, patientId, employer.trim());
      if (payor !== track) {
        await switchPatientTrack(medplum, patient, profile, track, payor, false, 'CoverageActivityBox');
      }
      const benefitCoverage = await resolveBenefitCoverage(medplum, patientId);
      if (!benefitCoverage) throw new Error('This patient’s coverage names no benefit administrator yet.');
      await recordCoverageAction(medplum, patient, benefitCoverage, providerOrganizationId, {
        action,
        date,
        note,
        documents,
      });
      await onRecorded?.();
    },
  };
}

/** The step as its Claim or ClaimResponse, written as the signed-in user; an answer points at the latest request. */
async function recordCoverageAction(
  medplum: MedplumClient,
  patient: Patient,
  benefitCoverage: BenefitCoverage,
  providerOrganizationId: string,
  { action, date, note, documents }: CoverageActivityValues
): Promise<void> {
  const coverageAction = action as CoverageAction;
  const request = isCoverageRequest(coverageAction)
    ? undefined
    : await findLatestCoverageRequest(medplum, patient.id as string, benefitCoverage.coverage);
  await medplum.createResource(
    buildCoverageActionRecord({
      patient: createReference(patient),
      benefitCoverage,
      provider: { reference: `Organization/${providerOrganizationId}` },
      action: coverageAction,
      on: date,
      note,
      documents,
      request,
    })
  );
}

/** The insurance card as on file, which the composer prefills and diffs against. */
export interface InsuranceOnFile {
  insurer: string | null;
  memberId: string;
  groupNumber: string;
}

/**
 * The insurance track's config (BAR-3080). On Record: a changed insurer, member ID or group ID
 * saves through `update-insurance-coverage`, so the side panel matches, then the step is written
 * against the episode's Coverage. Prior auth submitted is never gated.
 */
export function insuranceCoverageConfig(input: {
  medplum: MedplumClient;
  patient: Patient;
  onFile: InsuranceOnFile;
  edited: InsuranceOnFile;
  providerOrganizationId: string;
  trackFields?: ReactNode;
  onRecorded?: () => void | Promise<void>;
}): CoverageActivityConfig {
  const { medplum, patient, onFile, edited, providerOrganizationId, trackFields, onRecorded } = input;
  return {
    actions: INSURANCE_COVERAGE_ACTIONS,
    trackFields,
    ready: edited.insurer !== null,
    documentType: (action) => DOCUMENT_TYPE_BY_ACTION[action],
    onRecord: async (values) => {
      if (!edited.insurer) throw new Error('Select an insurer first');
      const memberId = edited.memberId.trim();
      const groupNumber = edited.groupNumber.trim();
      const edit: InsuranceEdit = {
        ...(edited.insurer !== onFile.insurer ? { insurer: { reference: edited.insurer } } : {}),
        ...(memberId !== onFile.memberId ? { memberId: memberId || null } : {}),
        ...(groupNumber !== onFile.groupNumber ? { groupNumber: groupNumber || null } : {}),
      };
      if (Object.keys(edit).length > 0) {
        await updateInsuranceCoverage(medplum, { patient: createReference(patient), ...edit });
        // The bot wrote the Coverage; a cached search would still name the old insurer.
        medplum.invalidateSearches('Coverage');
      }
      const benefitCoverage = await resolveBenefitCoverage(medplum, patient.id as string);
      if (!benefitCoverage) throw new Error('This patient’s coverage names no insurer yet.');
      await recordCoverageAction(medplum, patient, benefitCoverage, providerOrganizationId, values);
      await onRecorded?.();
    },
  };
}

const EMPTY_ON_FILE: InsuranceOnFile = { insurer: null, memberId: '', groupNumber: '' };

async function readInsuranceOnFile(medplum: MedplumClient, patient: Patient): Promise<InsuranceOnFile> {
  const read = await readInsuranceSection(medplum, patient);
  if (read.status !== 'found') return EMPTY_ON_FILE;
  const { insurer, memberId, groupNumber } = read.details;
  return {
    insurer: (insurer && getReferenceString(insurer)) ?? null,
    memberId: memberId ?? '',
    groupNumber: groupNumber ?? '',
  };
}

/** The coverage composer's config for the open patient's track (BAR-3075): employer or insurance. */
export function useCoverageActivityConfig(): CoverageActivityConfig | undefined {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const { patientContext, onCoverageActivityRecorded } = useTimelineContext();
  const patient = patientContext?.patient;
  const { track, loading: trackLoading } = usePatientTrack(patient?.id);
  const providerOrganizationId = getConfig().everselfOrganizationId;

  const [payor, setPayor] = useState<EmployerPayor | null>(null);
  const [employer, setEmployer] = useState('');
  // Prefilled from the side panel's facts, once per patient and once the track is known.
  useEffect(() => {
    if (trackLoading) return;
    setPayor(payorOf(track));
    setEmployer(patient ? (getExtension(patient, EMPLOYER_EXTENSION)?.valueString ?? '') : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset per patient, not on every track re-read
  }, [patient?.id, trackLoading]);

  const isInsurance = !trackLoading && track === Track.INSURANCE;
  const [onFile, setOnFile] = useState<InsuranceOnFile | undefined>(undefined);
  const [edited, setEdited] = useState<InsuranceOnFile>(EMPTY_ON_FILE);
  useEffect(() => {
    setOnFile(undefined);
    if (!isInsurance || !patient) return undefined;
    let cancelled = false;
    void readInsuranceOnFile(medplum, patient).then((read) => {
      if (cancelled) return;
      setOnFile(read);
      setEdited(read);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- read once per patient, not on every live update
  }, [medplum, patient?.id, isInsurance]);

  return useMemo(() => {
    if (!patient || trackLoading || !providerOrganizationId) return undefined;
    if (track === Track.INSURANCE) {
      if (!onFile) return undefined;
      return insuranceCoverageConfig({
        medplum,
        patient,
        onFile,
        edited,
        providerOrganizationId,
        trackFields: (
          <InsuranceCoverageFields
            insurer={edited.insurer}
            onInsurerChange={(insurer) => setEdited((current) => ({ ...current, insurer }))}
            memberId={edited.memberId}
            onMemberIdChange={(memberId) => setEdited((current) => ({ ...current, memberId }))}
            groupNumber={edited.groupNumber}
            onGroupNumberChange={(groupNumber) => setEdited((current) => ({ ...current, groupNumber }))}
          />
        ),
        onRecorded: onCoverageActivityRecorded,
      });
    }
    if (!EMPLOYER_TRACKS.has(track)) return undefined;
    return employerCoverageConfig({
      medplum,
      patient,
      profile,
      track,
      payor,
      employer,
      providerOrganizationId,
      trackFields: (
        <EmployerCoverageFields
          key={patient.id}
          payor={payor}
          onPayorChange={setPayor}
          employer={employer}
          onEmployerChange={setEmployer}
        />
      ),
      onRecorded: onCoverageActivityRecorded,
    });
  }, [
    medplum,
    patient,
    profile,
    track,
    trackLoading,
    payor,
    employer,
    providerOrganizationId,
    onFile,
    edited,
    onCoverageActivityRecorded,
  ]);
}
